import React from 'react';
import { X } from 'lucide-react';
import styles from '../../wizard-framework/WizardShell.module.css';

/**
 * WizardShell - Pure Presentational Component
 *
 * Reusable multi-step wizard UI container.
 * This is a PURE UI component - all logic lives in hooks (useWizardNavigation).
 *
 * Features:
 * - Progress indicator with step visualization
 * - Modal overlay with backdrop
 * - Responsive action buttons
 * - Theme-aware styling
 * - Fixed height to prevent content jumping
 *
 * Props Interface:
 * @param {string} title - Wizard title
 * @param {number} totalSteps - Total number of steps
 * @param {number} currentStep - Current step (1-indexed)
 * @param {ReactNode} children - Step content
 * @param {function} onNext - Next button handler
 * @param {function} onPrevious - Previous button handler
 * @param {function} onFinish - Finish button handler
 * @param {function} onClose - Close/Cancel handler
 * @param {object} buttonStates - Button visibility and state flags
 * @param {string} nextButtonLabel - Label for next button (default: 'Next')
 * @param {string} previousButtonLabel - Label for previous button (default: 'Back')
 * @param {string} finishButtonLabel - Label for finish button (default: 'Finish')
 * @param {boolean} isLoading - Loading state (default: false)
 */
const WizardShell = ({
  title,
  totalSteps,
  currentStep,
  children,
  onNext,
  onPrevious,
  onFinish,
  onClose,
  buttonStates = {},
  nextButtonLabel = 'Next',
  previousButtonLabel = 'Back',
  finishButtonLabel = 'Finish',
  isLoading = false
}) => {
  // Extract button states with defaults for backward compatibility
  const {
    canGoNext = true,
    canGoPrevious = true,
    showNext = currentStep < totalSteps,
    showPrevious = currentStep > 1,
    showFinish = currentStep === totalSteps
  } = buttonStates;

  return (
    <div className={styles.overlay}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerRow}>
            <h2 className={styles.title}>{title}</h2>
            <button
              className={styles.closeButton}
              onClick={onClose}
              disabled={isLoading}
              aria-label="Close wizard"
            >
              <X size={20} />
            </button>
          </div>

          {/* Progress Indicator */}
          <div className={styles.progressContainer}>
            <div className={styles.progressTrack}>
              {Array.from({ length: totalSteps }).map((_, index) => {
                const stepNumber = index + 1;
                const isCompleted = stepNumber < currentStep;
                const isCurrent = stepNumber === currentStep;
                const isPending = stepNumber > currentStep;

                return (
                  <React.Fragment key={index}>
                    <div
                      className={`
                        ${styles.stepIndicator}
                        ${isCompleted ? styles.completed : ''}
                        ${isCurrent ? styles.current : ''}
                        ${isPending ? styles.pending : ''}
                      `}
                      aria-label={`Step ${stepNumber}`}
                      aria-current={isCurrent ? 'step' : undefined}
                    >
                      {isCompleted ? '✓' : stepNumber}
                    </div>
                    {index < totalSteps - 1 && (
                      <div
                        className={`
                          ${styles.stepConnector}
                          ${isCompleted ? styles.completed : styles.pending}
                        `}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <span className={styles.progressLabel}>
              Step {currentStep} of {totalSteps}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {children}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            {showPrevious && (
              <button
                className={`${styles.button} ${styles.buttonOutline}`}
                onClick={onPrevious}
                disabled={!canGoPrevious || isLoading}
              >
                {previousButtonLabel}
              </button>
            )}
          </div>
          <div className={styles.footerRight}>
            <button
              className={`${styles.button} ${styles.buttonOutline}`}
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            {showNext && (
              <button
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={onNext}
                disabled={!canGoNext || isLoading}
              >
                {isLoading ? 'Processing...' : nextButtonLabel}
              </button>
            )}
            {showFinish && (
              <button
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={onFinish}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : finishButtonLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { WizardShell };
